/**
 * `/admin/users` view — system-admin user directory.
 *
 * Architecture mirrors `admin/signups-view.ts` + `admin/llm-view.ts`:
 *   - the view function produces the static landmark + heading skeleton +
 *     anchor nodes; a single `onMount` hook fetches the user list and
 *     populates the table imperatively. Route-local data, kept out of the
 *     global Model.
 *   - 401/404 → the shared not-found / sign-in overlay (`admin-overlay.ts`).
 *
 * Layout is master-detail: a server-searched, paginated user table on the
 * left; clicking a row lazy-fetches the full per-user detail into the
 * right-hand panel, which also carries the admin actions (promote/demote,
 * force sign-out, password reset, resend verification, cancel deletion). No
 * secrets are ever transported — the worker serialises only derived booleans
 * + non-sensitive metadata.
 *
 * Accessibility: `role="region"` panels with `aria-labelledby`, a
 * `role="status" aria-live="polite"` status node, `role="alert"` errors,
 * labelled inputs, the selected row carries `aria-current="true"`.
 */
import { pageHeader, textInput } from '@dl/ui';
import {
  div,
  label,
  main,
  p,
  section,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

import { maybeRenderOverlay as renderOverlay } from './admin-overlay.js';
import { onAdminMount } from './mount.js';
import {
  cancelDeletion,
  getUser,
  listUsers,
  resendVerification,
  revokeSessions,
  setSystemAdmin,
  triggerPasswordReset,
  type AdminResult,
  type UserDetail,
  type UserSummary,
} from './users-api.js';

const PAGE_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 250;

function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

function formatAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function el(
  tag: string,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const ch of children) {
    node.appendChild(typeof ch === 'string' ? document.createTextNode(ch) : ch);
  }
  return node;
}

/** A small pill paired with text (colour is never the only signal). */
function badge(labelText: string, kind: string): HTMLElement {
  return el('span', { class: 'admin-users-badge', 'data-kind': kind }, [labelText]);
}

function maybeRenderOverlay(root: Element, result: AdminResult<unknown>): boolean {
  return renderOverlay(root, result, {
    testid: 'admin-users-overlay',
    notFoundHeading: t('admin.users.not_found.heading'),
    notFoundBody: t('admin.users.not_found.body'),
    unauthHeading: t('admin.users.unauthenticated.heading'),
    unauthBody: t('admin.users.unauthenticated.body'),
    unauthLink: t('admin.users.unauthenticated.link'),
    signInTestid: 'admin-users-sign-in-link',
    hideSelector: '.admin-users-layout',
    introSelector: '.dl-page-header',
  });
}

// ───────────────────────────────────────────────────────── state cache ─

interface UsersCache {
  users: UserSummary[];
  total: number;
  offset: number;
  filter: string;
  selectedId: string | null;
}

const cache: UsersCache = { users: [], total: 0, offset: 0, filter: '', selectedId: null };

/** Root element handle, captured at mount so handlers can re-render. */
let rootEl: Element | null = null;

function summaryBadges(u: UserSummary): HTMLElement {
  const wrap = el('div', { class: 'admin-users-badges' });
  wrap.appendChild(
    u.is_system_admin
      ? badge(t('admin.users.badge.admin'), 'admin')
      : badge(t('admin.users.badge.member'), 'member'),
  );
  wrap.appendChild(
    u.email_verified
      ? badge(t('admin.users.badge.verified'), 'verified')
      : badge(t('admin.users.badge.unverified'), 'unverified'),
  );
  if (u.totp_enabled) wrap.appendChild(badge(t('admin.users.badge.twofa'), 'twofa'));
  if (u.pending_deletion) {
    wrap.appendChild(badge(t('admin.users.badge.pending_deletion'), 'pending-deletion'));
  }
  return wrap;
}

function renderTable(tableEl: Element, detailEl: Element): void {
  clear(tableEl);
  const rows = cache.users;
  if (rows.length === 0) {
    tableEl.appendChild(el('p', { class: 'admin-panel-status' }, [t('admin.users.empty')]));
    return;
  }

  const table = el('table', { class: 'admin-users-table', 'data-testid': 'admin-users-table' });
  const thead = el('thead');
  const headRow = el('tr');
  for (const key of ['email', 'status', 'campaigns', 'sessions', 'created'] as const) {
    headRow.appendChild(el('th', { scope: 'col' }, [t(`admin.users.col.${key}`)]));
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = el('tbody');
  for (const u of rows) {
    const tr = el('tr', {
      class: 'admin-users-row',
      'data-testid': `admin-users-row-${u.id}`,
      role: 'button',
      tabindex: '0',
      'data-user-id': u.id,
    });
    if (u.id === cache.selectedId) tr.setAttribute('aria-current', 'true');

    const emailCell = el('td', { class: 'admin-users-email-cell' });
    emailCell.appendChild(el('span', { class: 'admin-users-email' }, [u.email]));
    if (u.account_name) {
      emailCell.appendChild(el('span', { class: 'admin-users-account' }, [u.account_name]));
    }
    tr.appendChild(emailCell);

    tr.appendChild(el('td', {}, [summaryBadges(u)]));
    tr.appendChild(el('td', { class: 'admin-users-num' }, [String(u.campaign_count)]));
    tr.appendChild(el('td', { class: 'admin-users-num' }, [String(u.active_session_count)]));
    tr.appendChild(el('td', { class: 'admin-users-at' }, [formatAt(u.created_at)]));

    const open = (): void => {
      cache.selectedId = u.id;
      for (const r of tableEl.querySelectorAll('.admin-users-row')) {
        r.removeAttribute('aria-current');
      }
      tr.setAttribute('aria-current', 'true');
      void loadDetail(detailEl, u.id);
    };
    tr.addEventListener('click', open);
    tr.addEventListener('keydown', (ev) => {
      const key = (ev as KeyboardEvent).key;
      if (key === 'Enter' || key === ' ') {
        ev.preventDefault();
        open();
      }
    });
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  tableEl.appendChild(table);
}

function renderPagination(countEl: Element, controlsEl: Element, detailEl: Element): void {
  const from = cache.total === 0 ? 0 : cache.offset + 1;
  const to = Math.min(cache.offset + cache.users.length, cache.total);
  countEl.textContent = t('admin.users.count_range', { from, to, total: cache.total });

  clear(controlsEl);
  const prev = el('button', {
    type: 'button',
    class: 'admin-users-page-btn',
    'data-testid': 'admin-users-prev',
  }, [t('admin.users.prev')]);
  const next = el('button', {
    type: 'button',
    class: 'admin-users-page-btn',
    'data-testid': 'admin-users-next',
  }, [t('admin.users.next')]);
  if (cache.offset <= 0) (prev as HTMLButtonElement).disabled = true;
  if (cache.offset + cache.users.length >= cache.total) (next as HTMLButtonElement).disabled = true;
  prev.addEventListener('click', () => {
    cache.offset = Math.max(0, cache.offset - PAGE_LIMIT);
    void loadList(detailEl);
  });
  next.addEventListener('click', () => {
    if (cache.offset + cache.users.length < cache.total) {
      cache.offset += PAGE_LIMIT;
      void loadList(detailEl);
    }
  });
  controlsEl.appendChild(prev);
  controlsEl.appendChild(next);
}

let listToken = 0;

async function loadList(detailEl: Element): Promise<void> {
  const root = rootEl;
  if (!root) return;
  const myToken = ++listToken;
  const statusEl = root.querySelector('[data-users-status]');
  const tableEl = root.querySelector('[data-users-table]');
  const countEl = root.querySelector('[data-users-count]');
  const controlsEl = root.querySelector('[data-users-pagination]');
  if (statusEl) statusEl.textContent = t('admin.users.loading');

  const res = await listUsers({ q: cache.filter, limit: PAGE_LIMIT, offset: cache.offset });
  if (myToken !== listToken) return;
  if (maybeRenderOverlay(root, res)) return;
  if (!res.ok) {
    if (statusEl) statusEl.textContent = t('admin.users.error.generic');
    return;
  }
  if (statusEl) statusEl.textContent = '';
  cache.users = res.value.users;
  cache.total = res.value.total;
  cache.offset = res.value.offset;
  if (tableEl) renderTable(tableEl, detailEl);
  if (countEl && controlsEl) renderPagination(countEl, controlsEl, detailEl);
}

// ──────────────────────────────────────────────────────── detail panel ─

function detailField(labelText: string, value: string | Node): HTMLElement {
  const row = el('div', { class: 'admin-users-field' });
  row.appendChild(el('dt', {}, [labelText]));
  row.appendChild(
    el('dd', {}, [typeof value === 'string' ? document.createTextNode(value) : value]),
  );
  return row;
}

function detailSection(titleText: string): HTMLElement {
  return el('h3', { class: 'admin-users-detail-subheading' }, [titleText]);
}

function detailList(titleText: string, rows: HTMLElement[], emptyText: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.appendChild(detailSection(titleText));
  if (rows.length === 0) {
    frag.appendChild(el('p', { class: 'admin-panel-status' }, [emptyText]));
  } else {
    const ul = el('ul', { class: 'admin-users-detail-list', role: 'list' });
    for (const r of rows) ul.appendChild(r);
    frag.appendChild(ul);
  }
  return frag;
}

/** Build the admin actions block for a user. */
function renderActions(detailEl: Element, d: UserDetail): HTMLElement {
  const u = d.user;
  const wrap = el('section', {
    class: 'admin-users-actions',
    'aria-label': t('admin.users.actions.title'),
    'data-testid': 'admin-users-actions',
  });
  wrap.appendChild(detailSection(t('admin.users.actions.title')));
  const status = el('div', {
    'data-user-actions-status': '',
    class: 'admin-panel-status',
    role: 'status',
    'aria-live': 'polite',
  });
  const buttons = el('div', { class: 'admin-users-action-buttons' });

  const run = async (
    btn: HTMLButtonElement,
    confirmMsg: string | null,
    fn: () => Promise<AdminResult<unknown>>,
    okMsg: (v: unknown) => string,
    onOk: () => void,
  ): Promise<void> => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    btn.disabled = true;
    status.textContent = t('admin.users.actions.working');
    const res = await fn();
    btn.disabled = false;
    if (!res.ok) {
      status.textContent =
        res.kind === 'error' && res.status === 409
          ? res.error
          : t('admin.users.error.generic');
      return;
    }
    status.textContent = okMsg(res.value);
    onOk();
  };

  // Promote / demote system admin.
  const adminBtn = el('button', {
    type: 'button',
    class: 'admin-users-action',
    'data-testid': 'admin-users-action-system-admin',
  }, [u.is_system_admin ? t('admin.users.actions.demote') : t('admin.users.actions.promote')]) as HTMLButtonElement;
  adminBtn.addEventListener('click', () => {
    void run(
      adminBtn,
      u.is_system_admin
        ? t('admin.users.actions.demote_confirm', { email: u.email })
        : t('admin.users.actions.promote_confirm', { email: u.email }),
      () => setSystemAdmin(u.id, !u.is_system_admin),
      () => t('admin.users.actions.done'),
      () => {
        void loadDetail(detailEl, u.id);
        void loadList(detailEl);
      },
    );
  });
  buttons.appendChild(adminBtn);

  // Force sign-out.
  const revokeBtn = el('button', {
    type: 'button',
    class: 'admin-users-action',
    'data-testid': 'admin-users-action-revoke',
  }, [t('admin.users.actions.revoke')]) as HTMLButtonElement;
  revokeBtn.addEventListener('click', () => {
    void run(
      revokeBtn,
      t('admin.users.actions.revoke_confirm', { email: u.email }),
      () => revokeSessions(u.id),
      (v) => t('admin.users.actions.revoked', { n: (v as { revoked: number }).revoked }),
      () => {
        void loadDetail(detailEl, u.id);
        void loadList(detailEl);
      },
    );
  });
  buttons.appendChild(revokeBtn);

  // Password reset.
  const resetBtn = el('button', {
    type: 'button',
    class: 'admin-users-action',
    'data-testid': 'admin-users-action-password-reset',
  }, [t('admin.users.actions.password_reset')]) as HTMLButtonElement;
  resetBtn.addEventListener('click', () => {
    void run(
      resetBtn,
      null,
      () => triggerPasswordReset(u.id),
      () => t('admin.users.actions.email_sent'),
      () => undefined,
    );
  });
  buttons.appendChild(resetBtn);

  // Resend verification — only when the email is not yet verified.
  if (!u.email_verified_at) {
    const verifyBtn = el('button', {
      type: 'button',
      class: 'admin-users-action',
      'data-testid': 'admin-users-action-resend-verification',
    }, [t('admin.users.actions.resend_verification')]) as HTMLButtonElement;
    verifyBtn.addEventListener('click', () => {
      void run(
        verifyBtn,
        null,
        () => resendVerification(u.id),
        () => t('admin.users.actions.email_sent'),
        () => undefined,
      );
    });
    buttons.appendChild(verifyBtn);
  }

  // Cancel pending deletion — only when one is pending.
  if (u.pending_deletion_at) {
    const cancelBtn = el('button', {
      type: 'button',
      class: 'admin-users-action',
      'data-testid': 'admin-users-action-cancel-deletion',
    }, [t('admin.users.actions.cancel_deletion')]) as HTMLButtonElement;
    cancelBtn.addEventListener('click', () => {
      void run(
        cancelBtn,
        t('admin.users.actions.cancel_deletion_confirm', { email: u.email }),
        () => cancelDeletion(u.id),
        () => t('admin.users.actions.done'),
        () => {
          void loadDetail(detailEl, u.id);
          void loadList(detailEl);
        },
      );
    });
    buttons.appendChild(cancelBtn);
  }

  wrap.appendChild(buttons);
  wrap.appendChild(status);
  return wrap;
}

function renderDetail(detailEl: Element, d: UserDetail): void {
  clear(detailEl);
  const u = d.user;

  detailEl.appendChild(
    el('h2', { class: 'admin-users-detail-heading', 'data-testid': 'admin-users-detail-email' }, [
      u.email,
    ]),
  );
  detailEl.appendChild(summaryBadgesFromDetail(d));

  // ── Identity ──────────────────────────────────────────────────────
  const dl = el('dl', { class: 'admin-users-fields' });
  dl.appendChild(detailField(t('admin.users.detail.id'), u.id));
  if (d.account) {
    dl.appendChild(detailField(t('admin.users.detail.account'), d.account.name || '—'));
    dl.appendChild(detailField(t('admin.users.detail.account_id'), d.account.id));
  } else {
    dl.appendChild(detailField(t('admin.users.detail.account'), t('admin.users.detail.account_none')));
  }
  dl.appendChild(detailField(t('admin.users.detail.created'), formatAt(u.created_at)));
  dl.appendChild(
    detailField(
      t('admin.users.detail.verified'),
      u.email_verified_at ? formatAt(u.email_verified_at) : t('admin.users.no'),
    ),
  );
  dl.appendChild(
    detailField(
      t('admin.users.detail.recovery_email'),
      u.recovery_email ?? t('admin.users.detail.recovery_none'),
    ),
  );
  dl.appendChild(
    detailField(
      t('admin.users.detail.twofa'),
      u.totp_enabled_at
        ? t('admin.users.detail.twofa_on', { at: formatAt(u.totp_enabled_at) })
        : t('admin.users.no'),
    ),
  );
  if (u.totp_enabled_at) {
    dl.appendChild(detailField(t('admin.users.detail.backup_codes'), String(u.backup_codes_remaining)));
  }
  if (u.pending_deletion_at) {
    dl.appendChild(detailField(t('admin.users.detail.pending_deletion'), formatAt(u.pending_deletion_at)));
  }
  detailEl.appendChild(dl);

  // ── Actions ───────────────────────────────────────────────────────
  detailEl.appendChild(renderActions(detailEl, d));

  // ── Campaign memberships ──────────────────────────────────────────
  detailEl.appendChild(
    detailList(
      t('admin.users.detail.memberships'),
      d.memberships.map((m) => {
        const li = el('li', { class: 'admin-users-detail-row' });
        li.appendChild(el('span', { class: 'admin-users-detail-name' }, [m.campaign_name ?? m.campaign_id]));
        li.appendChild(
          badge(
            m.role === 'gm' ? t('admin.users.detail.role_gm') : t('admin.users.detail.role_player'),
            m.role === 'gm' ? 'admin' : 'member',
          ),
        );
        li.appendChild(el('span', { class: 'admin-users-at' }, [formatAt(m.added_at)]));
        return li;
      }),
      t('admin.users.detail.memberships_empty'),
    ),
  );

  // ── Owned campaigns ───────────────────────────────────────────────
  detailEl.appendChild(
    detailList(
      t('admin.users.detail.owned'),
      d.owned_campaigns.map((c) => {
        const li = el('li', { class: 'admin-users-detail-row' });
        li.appendChild(el('span', { class: 'admin-users-detail-name' }, [c.name]));
        li.appendChild(el('span', { class: 'admin-users-at' }, [formatAt(c.created_at)]));
        return li;
      }),
      t('admin.users.detail.owned_empty'),
    ),
  );

  // ── Sessions ──────────────────────────────────────────────────────
  detailEl.appendChild(
    detailList(
      t('admin.users.detail.sessions'),
      d.sessions.map((s) => {
        const li = el('li', { class: 'admin-users-detail-row' });
        const state = s.revoked_at
          ? t('admin.users.detail.session_revoked')
          : s.active
            ? t('admin.users.detail.session_active')
            : t('admin.users.detail.session_expired');
        li.appendChild(badge(state, s.active ? 'verified' : 'unverified'));
        li.appendChild(
          el('span', { class: 'admin-users-detail-name' }, [
            s.user_agent ?? t('admin.users.detail.session_unknown_agent'),
          ]),
        );
        li.appendChild(
          el('span', { class: 'admin-users-at' }, [
            t('admin.users.detail.session_seen', { at: formatAt(s.last_seen_at) }),
          ]),
        );
        return li;
      }),
      t('admin.users.detail.sessions_empty'),
    ),
  );

  // ── Recent emails ─────────────────────────────────────────────────
  detailEl.appendChild(
    detailList(
      t('admin.users.detail.emails'),
      d.emails.map((e) => {
        const li = el('li', { class: 'admin-users-detail-row' });
        li.appendChild(el('span', { class: 'admin-users-detail-name' }, [e.template]));
        li.appendChild(badge(e.outcome, e.outcome === 'sent' ? 'verified' : 'unverified'));
        li.appendChild(el('span', { class: 'admin-users-at' }, [formatAt(e.sent_at)]));
        return li;
      }),
      t('admin.users.detail.emails_empty'),
    ),
  );
}

function summaryBadgesFromDetail(d: UserDetail): HTMLElement {
  const wrap = el('div', { class: 'admin-users-badges' });
  wrap.appendChild(
    d.user.is_system_admin
      ? badge(t('admin.users.badge.admin'), 'admin')
      : badge(t('admin.users.badge.member'), 'member'),
  );
  wrap.appendChild(
    d.user.email_verified_at
      ? badge(t('admin.users.badge.verified'), 'verified')
      : badge(t('admin.users.badge.unverified'), 'unverified'),
  );
  if (d.user.totp_enabled_at) wrap.appendChild(badge(t('admin.users.badge.twofa'), 'twofa'));
  if (d.user.pending_deletion_at) {
    wrap.appendChild(badge(t('admin.users.badge.pending_deletion'), 'pending-deletion'));
  }
  return wrap;
}

let detailToken = 0;

async function loadDetail(detailEl: Element, id: string): Promise<void> {
  const myToken = ++detailToken;
  clear(detailEl);
  detailEl.appendChild(el('p', { class: 'admin-panel-status' }, [t('admin.users.detail.loading')]));
  const res = await getUser(id);
  if (myToken !== detailToken) return; // a newer selection superseded this
  if (!res.ok) {
    clear(detailEl);
    detailEl.appendChild(
      el('p', { class: 'admin-panel-status', role: 'alert' }, [t('admin.users.error.generic')]),
    );
    return;
  }
  renderDetail(detailEl, res.value);
}

// ──────────────────────────────────────────────────────────────── view ─

export function viewAdminUsers<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
): Mountable {
  cache.users = [];
  cache.total = 0;
  cache.offset = 0;
  cache.filter = '';
  cache.selectedId = null;
  rootEl = null;
  detailToken = 0;
  listToken = 0;

  const mountHook = onAdminMount('[data-admin-users-root]', (root) => {
    rootEl = root;
    const detailEl = root.querySelector('[data-users-detail]');
    const searchEl = root.querySelector<HTMLInputElement>('[data-users-search]');
    let debounce: ReturnType<typeof setTimeout> | undefined;

    if (searchEl && detailEl) {
      searchEl.addEventListener('input', () => {
        if (debounce) clearTimeout(debounce);
        debounce = setTimeout(() => {
          cache.filter = searchEl.value;
          cache.offset = 0;
          void loadList(detailEl);
        }, SEARCH_DEBOUNCE_MS);
      });
    }

    if (detailEl) void loadList(detailEl);

    return () => {
      if (debounce) clearTimeout(debounce);
      rootEl = null;
    };
  });

  return main(
    {
      role: 'main',
      'aria-labelledby': 'admin-users-heading',
      'data-admin-users-root': '',
      'data-testid': 'admin-users-view',
    },
    [
      mountHook,
      pageHeader({
        title: text(t('admin.users.title')),
        titleId: 'admin-users-heading',
        intro: text(t('admin.users.intro')),
      }),
      div(
        {
          'data-users-status': '',
          role: 'status',
          'aria-live': 'polite',
          class: 'admin-panel-status',
        },
        [text(t('admin.users.loading'))],
      ),
      div({ class: 'admin-users-layout' }, [
        section(
          {
            class: 'admin-panel admin-users-list-panel',
            'aria-labelledby': 'admin-users-list-heading',
          },
          [
            label({ for: 'admin-users-search', id: 'admin-users-list-heading' }, [
              text(t('admin.users.search_label')),
            ]),
            textInput({
              type: 'search',
              testid: 'admin-users-search',
              placeholder: t('admin.users.search_placeholder'),
              attrs: {
                id: 'admin-users-search',
                autocomplete: 'off',
                'data-users-search': '',
              },
            }),
            div({ class: 'admin-users-list-meta' }, [
              div(
                {
                  'data-users-count': '',
                  class: 'admin-users-count',
                  role: 'status',
                  'aria-live': 'polite',
                },
                [],
              ),
              div({ 'data-users-pagination': '', class: 'admin-users-pagination' }, []),
            ]),
            div({ 'data-users-table': '', class: 'admin-users-table-wrap' }, []),
          ],
        ),
        section(
          {
            class: 'admin-panel admin-users-detail-panel',
            'aria-labelledby': 'admin-users-detail-label',
            'data-users-detail': '',
            'data-testid': 'admin-users-detail',
          },
          [
            p({ id: 'admin-users-detail-label', class: 'admin-panel-status' }, [
              text(t('admin.users.select_prompt')),
            ]),
          ],
        ),
      ]),
    ],
  );
}
